<template>

    <div>
        <social-media-selector v-if="showSocialMediaLogins"
                               :types="availableSocialProviders"
                               :header-text="subPanelTranslations.register_header_text"
                               :use-account-text="subPanelTranslations.use_auth_provider_text"
                               :use-email-text="subPanelTranslations.use_email_text"
                               @change="authenticationSelected">
        </social-media-selector>

        <v-card flat :color="settings.surveyBoxColor" v-if="started && !welcomePageDisabled && !showSocialMediaLogins">
            <v-card-text>

                <!-- PROGRESS BAR -->
                <v-progress-linear
                    v-show="componentData.settings.show_progress && (activeQuestion || surveyComplete || saving)"
                    striped
                    rounded
                    height="20"
                    :value="progress"
                    :color="settings.actionButtonColor"
                ></v-progress-linear>

                <!-- DYNAMIC QUESTION COMPONENTS -->
                <component v-if="activeQuestion"
                           :key="activeQuestion.id"
                           v-model="activeQuestion.answer"
                           class="mt-5"
                           :component-data="componentData"
                           :settings="settings"
                           :background="settings.surveyBoxColor"
                           v-bind:is="ucFirst(activeQuestion.data_variable.type) + 'Question'"
                           :question="activeQuestion"
                           :translations="subPanelTranslations"
                           :next-button-text="subPanelTranslations.survey_next_button_text"
                           :back-button-text="subPanelTranslations.survey_back_button_text"
                           :skip-button-text="subPanelTranslations.survey_skip_button_text"
                           :back-button-enabled="!surveyEngine.isFirstQuestion()"
                           :locale="locale"
                           :required="activeQuestion.isAnswerRequired()"
                           @input="saveAnswer"
                           @back="backClicked"
                           @skip="skipClicked"
                           :error-messages="this.activeError"
                           :saving="saving || validatingEmail"
                ></component>

                <question-base :required="true"
                               class="mt-4"
                               :settings="settings"
                               :next-button-text="subPanelTranslations.survey_complete_registration_button_text"
                               :next-button-visible="completeButtonVisible"
                               :next-button-disabled="completeButtonDisabled"
                               :next-button-enabled="termsAccepted"
                               :back-button-visible="false"
                               :saving="saving"
                               @next="saveData">

                    <question-header
                        v-if="showPassword"
                        :background-color="settings.surveyBoxColor"
                        :question-text="subPanelTranslations.survey_password_question_text"
                        :button-color="settings.actionButtonColor"
                        :translations="subPanelTranslations"
                    ></question-header>

                    <question-header
                        v-if="surveyComplete && !componentData.settings.collect_password"
                        :background-color="settings.surveyBoxColor"
                        question-text=""
                        :button-color="settings.actionButtonColor"
                        :translations="subPanelTranslations"
                    ></question-header>

                    <password-field v-if="showPassword"
                                    v-model="password"
                                    class="mt-5"
                                    :label="subPanelTranslations.survey_password_field_label"
                                    :label-confirm="subPanelTranslations.survey_password_confirmation_field_label"
                                    :label-security-indicator="subPanelTranslations.survey_password_security_indicator_label"
                                    :validation-required-error="subPanelTranslations.survey_password_validation_required"
                                    :validation-complexity-error="subPanelTranslations.survey_password_validation_complexity"
                                    :validation-mismatch-error="subPanelTranslations.survey_password_validation_mismatch"
                                    @valid="setPasswordValid"
                                    :autoFocus="true"
                                    :background-color="settings.surveyBoxColor"
                                    :color="settings.actionButtonColor"
                                    :solo="true"
                    ></password-field>

                    <vue-recaptcha v-if="showRecaptcha"
                                   class="mb-4 ml-2"
                                   v-show="componentData.settings.use_recaptcha"
                                   @verify="captchaVerified"
                                   @expired="captchaExpired"
                                   :sitekey="componentData.settings.recaptcha_site_key"
                                   :loadRecaptchaScript="true"
                    ></vue-recaptcha>

                    <div class="d-flex pa-10 justify-center" v-if="!showPassword && !showRecaptcha && saving">
                        <v-progress-circular :color="settings.actionButtonColor"
                                             size="100"
                                             width="10"
                                             indeterminate
                        ></v-progress-circular>
                    </div>

                    <accept-condition-checkboxes v-if="!componentData.settings.disable_consent_checkboxes"
                                                 :sub-panel-translations="subPanelTranslations"
                                                 :visible="completeButtonVisible"
                                                 :background-color="settings.surveyBoxColor"
                                                 :color="settings.actionButtonColor"
                                                 :sub-panel-id="subPanelId"
                                                 :locale-code="locale.code"
                                                 :terms-and-conditions-url="termsAndConditionsUrl"
                                                 :privacy-policy-url="privacyPolicyUrl"
                                                 :cookie-policy-url="cookiePolicyUrl"
                                                 @accepted="termsAcceptedChanged"
                                                 class="ml-2 mt-4 mb-6"
                    ></accept-condition-checkboxes>

                </question-base>

            </v-card-text>
        </v-card>

        <div v-if="!welcomePageDisabled && !showSocialMediaLogins">

            <!-- START REGISTRATION SURVEY BUTTON EDIT MODE-->
            <div v-if="editMode">
                <text-editor
                    v-model="startRegistrationButtonTextModel"
                    hint="Start registration button text">

                    <div>
                        <ninja-button :settings="settings">
                            {{ startRegistrationButtonTextModel }}
                        </ninja-button>
                    </div>

                </text-editor>
            </div>

            <!-- START REGISTRATION BUTTON RENDER MODE -->
            <div v-if="!editMode" class="d-flex justify-md-space-between justify-sm-center justify-center">

                <ninja-button
                    v-if="!welcomePageDisabled"
                    v-show="!started"
                    @click="selectAuthenticationMethod"
                    :settings="settings"
                    :disabled="!fingerprint"
                    :loading="surveyStarting"
                    class="mk-md-0"
                >
                    {{ subPanelTranslations.start_registration_button_text }}
                </ninja-button>
            </div>

        </div>

        <!-- BOTTOM SHEET FOR TEST DATA -->
        <v-dialog v-model="bottomSheetModel"
                  v-if="isTestMode">

            <test-data :test-data="componentData.testData"
                       :fingerprint="fingerprint"
                       :research-defender="researchDefender"/>
        </v-dialog>

        <debug v-if="false" :data="componentData.settings"/>

    </div>
</template>

<script>

import CheckboxQuestion from "@/components/UI/DynamicProflingQuestions/CheckboxQuestion.vue";
import DateQuestion from "@/components/UI/DynamicProflingQuestions/DateQuestion.vue";
import EmailQuestion from "@/components/UI/DynamicProflingQuestions/EmailQuestion.vue";
import KeywordQuestion from "@/components/UI/DynamicProflingQuestions/KeywordQuestion.vue";
import LocaleQuestion from "@/components/UI/DynamicProflingQuestions/LocaleQuestion.vue";
import NumberQuestion from "@/components/UI/DynamicProflingQuestions/NumberQuestion.vue";
import RadioQuestion from "@/components/UI/DynamicProflingQuestions/RadioQuestion.vue";
import TextQuestion from "@/components/UI/DynamicProflingQuestions/TextQuestion.vue";
import PhoneQuestion from "@/components/UI/DynamicProflingQuestions/PhoneQuestion.vue";
import TitleCardList from "@/components/UI/TitleCardList.vue";
import LabelValue from "@/components/UI/LabelValue.vue";
import TestData from "@/components/UI/LandingPage/TestData.vue";
import TextEditor from "@/components/UI/TextEditor.vue";
import VueRecaptcha from 'vue-recaptcha';
import {fingerprint} from "@/components/mixins/fingerprintMixin.js";
import {registrationQuestionsMixin} from "@/components/mixins/registrationQuestionsMixin.js";
import QuestionBase from "@/components/UI/DynamicProflingQuestions/QuestionBase.vue";
import QuestionHeader from "@/components/UI/DynamicProflingQuestions/QuestionHeader.vue";
import PasswordField from "@/components/UI/PasswordField.vue";
import Api from "@/api.js";
import SurveyEngine from "@/modules/RegistrationSurvey/ts/SurveyEngine.ts";
import Question from "@/modules/RegistrationSurvey/ts/Question.ts";
import PolicyLinkHelperMixin from "@/components/mixins/policyLinkHelperMixin.js";
import AcceptConditionCheckboxes from "@/modules/RegistrationSurvey/components/AcceptConditionCheckboxes.vue";
import researchDefenderMixin from "@/components/mixins/researchDefenderMixin.js";
import Buttons from "@/components/UI/LandingPage/Buttons.vue";
import NinjaButton from "@/components/UI/NinjaButton.vue";
import Debug from "@/components/UI/Debug.vue";
import SocialMediaSelector from "@/modules/RegistrationSurvey/components/SocialMediaSelector.vue";
import CircleLoader from "@/components/UI/CircleLoader.vue";

export default {

    name: "RegistrationSurvey",

    mixins: [
        fingerprint,
        registrationQuestionsMixin,
        researchDefenderMixin,
    ],

    components: {
        CircleLoader,
        SocialMediaSelector,
        Debug,
        NinjaButton,
        Buttons,
        AcceptConditionCheckboxes,
        PolicyLinkHelperMixin,
        PasswordField,
        QuestionHeader,
        QuestionBase,
        CheckboxQuestion,
        DateQuestion,
        EmailQuestion,
        KeywordQuestion,
        LocaleQuestion,
        NumberQuestion,
        RadioQuestion,
        TextQuestion,
        PhoneQuestion,
        TitleCardList,
        LabelValue,
        TestData,
        TextEditor,
        VueRecaptcha
    },

    props: {

        locale: {
            type: Object,
            required: true,
        },
        settings: {
            type: Object,
            required: true
        },
        componentData: {
            type: Object,
            required: true,
        },
        subPanelTranslations: {
            type: Object,
            required: true
        },
        editMode: {
            type: Boolean,
            required: false,
            default: false,
        },
        subPanelId: {
            type: Number,
            required: false,
            default: null,
        },
        showTestData: {
            type: Boolean,
            required: false,
            default: null,
        },
        termsAndConditionsUrl: {
            type: String | null,
            required: false,
            default: null
        },
        privacyPolicyUrl: {
            type: String | null,
            required: false,
            default: null
        },
        cookiePolicyUrl: {
            type: String | null,
            required: false,
            default: null
        },
    },

    data() {
        return {
            activeQuestion: null,
            activeError: null,
            started: false,
            saving: false,
            surveyStarting: false,
            validatingEmail: false,
            surveyEngine: null,

            // Emitted data
            answers: {},
            passwordValid: false,
            password: null,
            captchaToken: null,
            testDataButtonModel: true,
            fingerprint: null,
            eventCategory: null,
            emailValidated: false,
            termsAccepted: false,

            // Skipping welcome page
            welcomePageDisabled: false,
            showSocialMediaLogins: false,
        }
    },

    created() {

        if (!this.editMode) {
            this.surveyEngine = new SurveyEngine(this.createQuestions());
            this.validateEmailIfProvided();
        }

        this.welcomePageDisabled = this.componentData.settings.disable_welcome_page;
    },

    mounted() {

        if (!this.editMode) {

            this.eventCategory = 'registration-' + this.subPanelId + '-' + this.locale.code;

            this.$nextTick(() => {
                if (window.Spark.googleAnalyticsUA) {
                    this.$gtag.event('welcome', {
                        'event_category': this.eventCategory,
                        'event_label': 'welcome',
                    });
                }
            });

            this.generateFingerprint(data => {
                this.fingerprint = data.result;

                if (this.componentData.settings.disable_welcome_page) {
                    this.selectAuthenticationMethod();
                }
            });
        }
    },

    computed: {

        startRegistrationButtonTextModel: {
            get() {
                return this.subPanelTranslations.start_registration_button_text;
            },
            set(value) {
                this.$emit('change:subPanelTranslations', {start_registration_button_text: value});
            }
        },

        bottomSheetModel: {
            get() {
                return this.showTestData;
            },
            set() {
                this.$emit('test-data-closed');
            }
        },

        availableSocialProviders() {
            return this.componentData.settings.social_auth_providers;
        },

        surveyComplete() {
            return this.surveyEngine.isComplete();
        },

        progress() {
            return this.surveyEngine.getProgressPercent();
        },

        showRecaptcha() {

            if (this.surveyComplete && this.surveyEngine.isDisqualified()) {
                return false;
            }

            return this.surveyComplete && this.componentData.settings.use_recaptcha;
        },

        showPassword() {

            if (this.surveyComplete && this.surveyEngine.isDisqualified()) {
                return false;
            }

            return this.surveyComplete && this.componentData.settings.collect_password;
        },

        isTestMode() {
            if (this.editMode) {
                return false;
            }
            return !!this.componentData.testData;
        },

        completeButtonDisabled() {

            if (this.componentData.settings.disable_consent_checkboxes) {
                return false;
            }

            return !this.termsAccepted;
        },

        completeButtonVisible() {
            return this.isPasswordEntered && this.isCaptchaEntered && this.surveyComplete;
        },

        isPasswordEntered() {
            if (!this.componentData.settings.collect_password) return true;
            return this.passwordValid;
        },

        isCaptchaEntered() {
            if (!this.componentData.settings.use_recaptcha) return true;
            return !!this.captchaToken;
        }
    },

    methods: {

        createQuestions() {

            let questions = [];

            if (!this.componentData.questions) {
                throw new Error("No questions found in the component data");
            }

            this.componentData.questions.forEach(serverQuestion => {

                // Set answer to the question object
                if (this.componentData.answers[serverQuestion.data_variable.name]) {
                    serverQuestion.answer = this.componentData.answers[serverQuestion.data_variable.name];
                }

                questions.push(new Question(serverQuestion))
            })

            return questions;
        },

        validateEmailIfProvided() {
            const emailQuestion = this.componentData.questions.find(question => question.data_variable.name === 'EMAIL');

            if (emailQuestion && this.answers['EMAIL']) {
                this.validateEmail(this.answers['EMAIL'].toLowerCase());
            }
        },

        selectAuthenticationMethod() {

            if (this.socialMediaAuthenticationsEnabled()) {
                this.showSocialMediaLogins = true;
                this.$emit('started', true);
                return;
            }

            this.beginSurvey();
        },

        socialMediaAuthenticationsEnabled() {

            if (!this.componentData.settings.social_auth_providers) {
                return false;
            }

            if (this.componentData.settings.social_auth_providers.length === 0) {
                return false;
            }

            if (this.componentData.settings.social_auth_completed) {
                return false;
            }

            return true;
        },

        authenticationSelected(provider) {

            if (provider === 'email') {
                this.beginSurvey();
                return;
            }

            this.sendToUrl('/auth/' + provider + "/redirect/" + this.subPanelId + '/' + this.locale.code + '?type=registration');
        },

        beginSurvey() {

            this.surveyStarting = true;

            if (this.componentData.settings.rd_enabled) {

                this.runResearchDefender(this.componentData.survey_id, this.componentData.session_id)
                    .then(() => {
                        this.submitStart();
                    })
                    .catch((error) => {
                        console.error("Research Defender failed to execute");
                        this.submitStart();
                    })
            } else {

                this.submitStart();
            }
        },

        submitStart() {

            let data = {
                signature: this.fingerprint,
                rd_data: btoa(JSON.stringify(this.researchDefender))
            }

            Api.put('/registration/start', data, {timeout: 5000})
                .then(response => {

                    this.started = true;
                    this.welcomePageDisabled = false;
                    this.showSocialMediaLogins = false;
                    this.$emit('hide-loader');
                    this.$emit('started');

                    if (window.Spark.googleAnalyticsUA) {
                        this.$gtag.event('start', {
                            'event_category': this.eventCategory,
                            'event_label': 'registration-survey-start',
                        });
                    }

                    this.activeQuestion = this.surveyEngine.getNextQuestion();
                })
                .catch(error => {

                    if (error.response && error.response.status === 412) {

                        this.sendToUrl(error.response.data.redirect);

                    } else {
                        console.error(error.toJSON());

                        Bus.$emit('flash-message', {
                            type: 'error',
                            message: this.subPanelTranslations.network_error
                        });
                    }
                })
                .finally(() => {
                    this.surveyStarting = false;
                })
        },

        saveAnswer(answer) {

            if (this.activeQuestion.data_variable.name === "EMAIL") {

                this.validateEmail(answer);

                if (this.componentData.rf_enabled) {
                    this.runResearchDefender();
                }

            } else {
                this.surveyEngine.saveAnswer(answer);
                this.activeQuestion = this.surveyEngine.getNextQuestion();
            }

            this.handleDisqualification();
        },

        handleDisqualification() {
            if (this.surveyEngine.isComplete() && this.surveyEngine.isDisqualified()) {
                this.saveData(true);
            }
        },

        getNextQuestion() {
            const question = this.surveyEngine.getNextQuestion();

            if (question !== null) {
                this.activeQuestion = question;
            }
        },

        backClicked() {
            this.activeQuestion = this.surveyEngine.getPreviousQuestion();
        },

        skipClicked() {
            this.surveyEngine.skipQuestion();
            this.activeQuestion = this.surveyEngine.getNextQuestion();
        },

        setPasswordValid(bool) {
            this.passwordValid = bool;
        },

        captchaVerified(captchaToken) {
            this.captchaToken = captchaToken;
        },

        captchaExpired() {
            this.captchaToken = null;
        },

        termsAcceptedChanged(bool) {
            this.termsAccepted = bool;
        },

        validateEmail(answer) {

            this.validatingEmail = true;

            Api.post('/registration/validate-email', {email: answer}, {timeout: 5000})

                .then(({data}) => {

                    if (data.redirect_url) {
                        this.sendToUrl(data.redirect_url);
                        return;
                    }

                    if (data.available === true) {
                        this.emailValidated = true;
                    } else {
                        this.emailValidated = false;
                        this.activeError = this.subPanelTranslations.survey_duplicate_email_error;
                    }

                    if (this.emailValidated) {
                        this.surveyEngine.saveAnswer(answer);
                        this.activeQuestion = this.surveyEngine.getNextQuestion();
                    }
                })
                .catch(error => {

                    if (error.code && error.code === 'ECONNABORTED') {

                        Bus.$emit('flash-message', {
                            type: 'error',
                            message: this.subPanelTranslations.network_timeout_error
                        });

                    } else if (error.response) {

                        if (error.response.status === 422) {

                            this.activeError = this.subPanelTranslations.survey_invalid_email_error;

                        } else if (error.response.status === 429) {
                            Bus.$emit('flash-message', {
                                type: 'error',
                                message: this.subPanelTranslations.network_too_many_requests_error
                            });
                        }

                    } else {

                        console.error(error);

                        Bus.$emit('flash-message', {
                            type: 'error',
                            message: this.subPanelTranslations.network_error
                        });
                    }
                })
                .finally(() => {
                    this.validatingEmail = false;
                });
        },

        saveData(disqualified = false) {

            const data = {
                answers: this.surveyEngine.getAnswers(),
                fingerprint: this.fingerprint,
            };

            // Do not submit captcha token when disqualified
            if (this.componentData.settings.use_recaptcha && !disqualified) {
                data.captchaToken = this.captchaToken;
            }

            // Do not submit password when disqualified
            if (this.componentData.settings.collect_password && !disqualified) {
                data.password = this.password;
            }

            this.saving = true;

            Api.post('/registration/answers', data, {timeout: 5000})

                .then((response) => {

                    if (window.Spark.googleAnalyticsUA) {

                        if (!disqualified) {

                            this.$gtag.event('complete', {
                                'event_category': this.eventCategory,
                                'event_label': 'complete',
                            });

                            this.$gtag.event('sign_up');

                        } else {

                            this.$gtag.event('disqualified', {
                                'event_category': this.eventCategory,
                                'event_label': 'disqualified',
                            });
                        }
                    }

                    this.sendToUrl(response.data.redirect);
                })
                .catch(error => {

                    if (error.code && error.code === 'ECONNABORTED') {

                        Bus.$emit('flash-message', {
                            type: 'error',
                            message: this.subPanelTranslations.network_timeout_error
                        });

                    } else if (error.response) {

                        if (error.response.status === 429) {

                            Bus.$emit('flash-message', {
                                type: 'error',
                                message: this.subPanelTranslations.network_too_many_requests_error
                            });

                        } else if (error.response && error.response.status === 422) {

                            Bus.$emit('flash-message', {
                                type: 'error',
                                message: error.response.data.message
                            });

                        } else if (error.response.status === 401) {

                            this.sendToUrl('/landing-page/security/' + this.subPanelId + '/' + this.locale.code);
                        }

                    } else {

                        console.log(error);

                        Bus.$emit('flash-message', {
                            type: 'error',
                            message: this.subPanelTranslations.network_error
                        });
                    }
                })
                .finally(() => {
                    this.saving = false;
                })
        }
    }
}
</script>
